import { yupResolver } from '@hookform/resolvers/yup';
import {
  useSingleFactorSignIn,
  useSingleFactorValidateCode,
} from '@sunrise-ui/api-client';
import {
  Button,
  ButtonVariants,
  Colors,
  InputError,
  OTPInput,
  TextLight,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  useSnackbar,
} from '@sunrise-ui/primitives';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { object, string, type InferType } from 'yup';

const schema = object().shape({
  code: string()
    .required('Code is required')
    .length(6, 'Must be 6 digits long'),
});

type FormValues = InferType<typeof schema>;

export function ConfirmEmailPage() {
  const { showSnackbar } = useSnackbar();
  const { authModal, updateAuthModal } = useAuthStore();
  const { codeSent, email, previousStep } = authModal;
  const styles = useDynamicStyles(createStyles);
  const [apiError, setApiError] = useState<string | null>(null);
  const { mutate: signIn, isPending: isSendingEmail } = useSingleFactorSignIn();
  const { mutateAsync: validateCode } = useSingleFactorValidateCode();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { code: '' },
    resolver: yupResolver(schema),
  });

  // Auto-send code when email is present and hasn't been sent yet
  useEffect(() => {
    if (email && !codeSent) {
      void signIn(
        { email },
        {
          onSuccess: () => {
            updateAuthModal({ codeSent: true });
          },
        },
      );
    }
  }, [email, codeSent, signIn, updateAuthModal]);

  const onSubmit = async ({ code }: FormValues) => {
    setApiError(null); // Clear any previous API errors
    if (email) {
      validateCode({ code, email }).catch(() => {
        // Handle error locally instead of showing snackbar
        setApiError('Invalid code. Please try again.');
      });
    } else {
      showSnackbar({ type: 'error', text: 'An error occurred' });
      updateAuthModal({
        currentStep: previousStep || 'login',
        codeSent: false,
      });
    }
  };

  const handleResend = () => {
    if (email) {
      signIn(
        { email },
        {
          onSuccess: () => {
            updateAuthModal({ codeSent: true });
            showSnackbar({
              type: 'info',
              text: 'New email sent!',
            });
          },
        },
      );
    } else {
      showSnackbar({ type: 'error', text: 'An error occurred' });
      updateAuthModal({
        currentStep: previousStep || 'login',
        codeSent: false,
      });
    }
  };

  return (
    <View style={styles.container}>
      <View>
        <Controller
          control={control}
          render={({ field: { onChange, value } }) => (
            <OTPInput
              value={value}
              onChange={(text) => {
                onChange(text);
                if (apiError) {
                  setApiError(null);
                }
              }}
              disabled={isSubmitting}
              error={!!errors.code || !!apiError}
            />
          )}
          name="code"
        />
        <InputError
          fieldError={
            errors.code || (apiError ? { message: apiError } : undefined)
          }
        />
      </View>
      <Button
        variant={ButtonVariants.PURPLE}
        size="medium"
        onPress={handleSubmit(onSubmit)}
        disabled={isSubmitting}
        loading={isSubmitting}
        style={styles.button}
      >
        Confirm
      </Button>
      <View style={styles.resendContainer}>
        <TextLight>
          Didn&apos;t receive code?{' '}
          <TextReg
            onPress={handleResend}
            style={styles.resendLink}
            disabled={isSendingEmail}
          >
            Resend
          </TextReg>
        </TextLight>
      </View>
    </View>
  );
}

const createStyles = () =>
  StyleSheet.create({
    container: {
      backgroundColor: Colors.purple0,
      gap: 16,
    },
    resendContainer: {
      alignItems: 'center',
    },
    button: {
      alignSelf: 'center',
    },
    resendLink: {
      color: Colors.purple1,
    },
  });
